import FearGreedCard from '@/features/market-rail/components/FearGreedCard'
import MarketUpdatesCard from '@/features/market-rail/components/MarketUpdatesCard'
import PopularStocksCard from '@/features/market-rail/components/PopularStocksCard'
import TopMoversCard from '@/features/market-rail/components/TopMoversCard'
import YearRangeCard from '@/features/market-rail/components/YearRangeCard'
import { useIsMarketRoute } from '@/features/market-rail/marketRoute'
import { cn } from '@/lib/utils'

export interface MarketRailProps {
  className?: string
}

/**
 * The shared right rail: Market news and Popular stocks on every screen (2026-09-28 design
 * refresh) — plus, on `/market` and its tool pages only, a Fear and greed reading between
 * Market news and Popular stocks (owner ask, Issue 7, 2026-09-29).
 *
 * No ask field at the bottom any more (owner, 2026-10-17): the Ask Mitra bar docked at the foot
 * of the window, beside it, is already the way in, and its chip names the page itself
 * ("Portfolio · Holdings") — see `MitraDock`.
 *
 * ── What changed from the pre-refresh rail ────────────────────────────────────────────────
 *
 * `MarketNowCard` (the NEPSE index card) and the closing footnote are still gone. The live
 * design's own rail crop (`4236:69879`) — the DEFAULT rail's own source, not a market-specific
 * one — draws only Market news and Popular stocks, with no NEPSE card and no line under the
 * last row; Home's own NEPSE chart lives in the MAIN column instead (`IndexTodayCard`), not the
 * rail. `MarketNowCard.tsx` is deleted (nothing else imported it — grepped before deleting, per
 * the wave brief).
 *
 * `MoodCard.tsx` is deleted too, and stays that way — the reading below is not its
 * resurrection. `MoodCard` drew unconditionally, on every screen; `FearGreedCard` draws only
 * where the owner asked for it, gates its own route and its own query (see `Data`, below), and
 * is new code built for this ask rather than the old file brought back.
 *
 * `MoodGauge` is kept, and now has two readers instead of one: Stock's compact fear-and-greed
 * reading (`StockRail`'s own `CompanyMoodBlock`) already read it before this ask, and
 * `FearGreedCard` (below) is its second reader — both five-band, the market's sized by mm-be's own
 * cuts (`MoodGauge`'s own header comment has the band-count history).
 * `RailFootnote` is still unused by this file: it is `ScreenLayout`'s own `footnote` prop for a
 * screen that hands in its OWN rail rather than this shared one.
 *
 * This component's own `footnote` prop is gone too; `ScreenLayout`'s
 * `footnote` now only closes a screen's own rail.
 *
 * ── What it does NOT know ─────────────────────────────────────────────────
 *
 * Its own position or width. The layout owns the grid — 240 sidebar | 32 |
 * 844 main | 32 | 260 rail | 32 at 1440, the rail 260 up to ~1950 then growing gently
 * (`--screen-rail-w`), the main column taking the rest — and renders this into the rail track as a
 * slot. Nothing here reads `--screen-rail-w`, sets a width, or sticks: a
 * component that pinned its own column would fight whatever the layout does
 * at the breakpoint where the rail stops being a column at all. `ScreenLayout`
 * pins the whole `<aside>`, as it pins every screen's rail (see its own
 * file); no card in here pins itself.
 *
 * `gap-10` (40px): the revamp mock's gap between the rail's blocks (Home mock
 * parity, 2026-10-28; it was the design refresh's 32 before), the same on every rail.
 *
 * ── Fitting the viewport, and how it is bought ────────────────────────────
 *
 * The rail is pinned under the header and never moves, and it always fits:
 * `ScreenLayout` measures the space between the pin and the Mitra dock and
 * fits every rail to it (`useRailFit`). Each list draws as many rows as the
 * space pays for — down to one, never more than the design's four and five —
 * and on a window too short even for that, whole blocks leave in
 * `RAIL_DROP_ORDER`: Fear and greed first, then Popular stocks, then Market
 * news. Row counts and blocks are what give, never the gap or a card's
 * padding, and "See all" on each list keeps the rest one click away.
 *
 * ── Data ──────────────────────────────────────────────────────────────────────────────────
 *
 * Two raw endpoints, both public (`V5PublicAPIView`, `AllowAny`, no `request.user`), read
 * through `src/api/endpointCache.ts`, on every screen:
 *
 *   market-updates  railService.updates()    → updates
 *   popular-stocks  usePopularStocks()       → movers
 *
 * A third joins the two above only on `/market` and `/market/*`:
 *
 *   market-summary  marketService.mood()     → mood (market routes only)
 *
 * `FearGreedCard` (below) checks the route with the router's own state before it calls
 * `useMarketMood()` at all, so `mood` is never an extra request on a screen this rail draws
 * with no reading to show. `market-now` (the NEPSE index figure) is still not fetched here on
 * any screen — see the note above on why that card left the default rail.
 */
export default function MarketRail({ className }: MarketRailProps) {
  // On `/market/*` the rail is the mock's Market rail (2026-10-28): Market news, Fear and greed,
  // Top movers (2 + 2) in Popular stocks' place, and NEPSE's 52-week range. Everywhere else it
  // stays news and Popular stocks.
  const market = useIsMarketRoute()

  return (
    // `complementary`, not `region`: the rail supports whatever screen it is
    // on and is meaningful apart from it, which is exactly what <aside> is
    // for. Labelled, because the page now has more than one landmark.
    <aside aria-label="Market rail" className={cn('flex flex-col gap-10', className)}>
      <MarketUpdatesCard />
      <FearGreedCard />
      {market ? (
        <>
          <TopMoversCard />
          <YearRangeCard />
        </>
      ) : (
        <PopularStocksCard />
      )}
    </aside>
  )
}
